import { useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import moment from 'moment'

import { useServiceScheduleContext } from '../../../context/serviceScheduleContext'
import { PAGE_URL, isEntityEnabledForPayment } from 'src/utils'
import { SCHEDULING_STEPS, PURCHASED_PACKAGE_SCHEDULING_STEPS } from '../../../utils/schedulingConstants'
import useFormStepCommon from './useFormStepCommon'

const ONLINE_PAYMENT_DEADLINE = process.env.ONLINE_PAYMENT_DEADLINE
const SCHEDULING_MIN_HOURS = process.env.SCHEDULING_MIN_HOURS

const useSchedulingFormStep = ({ finalStep, context }) => {
  const { state, functions, editSteps } = useServiceScheduleContext()
  const {
    review,
    isHowToPay,
    invertStep,
    scheduleBody,
    professionalData,
    partnersData,
    formList,
    selectedPurchasedPackage
  } = state

  const {
    pricesInfo,
    handleAddPetToScheduling,
    handleConfirmScheduling,
    findPartnerScheduleAndCampaignAviability,
    hasSchedulingChanged
  } = functions

  const router = useRouter()

  const {
    activeStep,
    numberOfSteps,
    currentStep,
    nextDisabled,
    nextHidden,
    steps,
    backButtonProps,
    nextButtonProps,
    onUpdateScheduling,
    onGoTo,
    redirectToServices
  } = useFormStepCommon({ context })

  const scheduleForMoreOnePetProps = {
    border: 'var(--cobasi-blue-light-9)',
    label: 'Agendar mais 1 pet',
    disabled: context.isLoading
  }

  const confirmScheduleProps = {
    label: 'Confirmar agendamento',
    disabled: context.isLoading
  }

  const reviewStep = useMemo(() =>
    scheduleBody?.isSchedulingFromPackage
      ? PURCHASED_PACKAGE_SCHEDULING_STEPS.REVIEW
      : SCHEDULING_STEPS.REVIEW,
  [scheduleBody?.isSchedulingFromPackage]
  )

  const filterRemainingSteps = (actualStep) => {
    let stepsToCheck = steps

    if (invertStep && scheduleBody.selectTime && scheduleBody.selectDay) {
      stepsToCheck = stepsToCheck.map(item => item.path === '/data' ? ({ ...item, active: true }) : item)
    }

    return stepsToCheck
      .filter(step => step.step > actualStep && !step.active && step.step !== SCHEDULING_STEPS.HOW_TO_PAY)
      .sort((a, b) => a.step - b.step)
  }

  const handleReviewStepNavigation = (remainingSteps, reviewStep) => {
    if (remainingSteps.length === 1 && remainingSteps[0].step === reviewStep) {
      const nextStep = scheduleBody?.isSchedulingFromPackage
        ? PURCHASED_PACKAGE_SCHEDULING_STEPS.REVIEW - 1
        : steps.length - 2

      onUpdateScheduling({ activeStep: nextStep, review: true })
      onGoTo(steps[nextStep].path)
      return true
    }
    return false
  }

  const calcNextStep = (actualStep) => {
    const remainingSteps = filterRemainingSteps(actualStep)

    if (handleReviewStepNavigation(remainingSteps, reviewStep)) return

    if (!remainingSteps.length) {
      onUpdateScheduling({
        activeStep: steps.length - 1,
        isHowToPay: true,
        review: false
      })

      onGoTo(steps[steps.length - 1].path)
      return
    }

    onUpdateScheduling({
      activeStep: remainingSteps[0]?.step
    })

    onGoTo(remainingSteps[0]?.path)
  }

  const editScheduling = (step = SCHEDULING_STEPS.PET_AND_SERVICE, isCustomState = false) => {
    const redirectedStep = steps.find(schedulingStep => schedulingStep.step === step)
    onUpdateScheduling({
      ...editSteps()[redirectedStep.id],
      ...(isCustomState && {
        activeStep: SCHEDULING_STEPS.PARTNER,
        scheduleBody: {
          ...editSteps()[redirectedStep.id].scheduleBody,
          professional_id: ''
        }
      })
    })

    if (isCustomState) {
      const locationStep = steps.find(schedulingStep => schedulingStep.step === 2)

      onGoTo(locationStep.path)
    } else {
      onGoTo(redirectedStep.path)
    }
  }

  useEffect(() => {
    if (scheduleBody?.isServicePackage) return

    if (!router.route.includes(steps[activeStep - 1].path)) {
      onGoTo(steps.find(item => item.step === (activeStep))?.path)
    }
  }, [invertStep])

  const handleClickBackButton = async () => {
    if (activeStep === SCHEDULING_STEPS.PET_AND_SERVICE) {
      const petUnselectedAndFormList = !scheduleBody.pet_id && state.formList.length
      const handleRedirectPetStep = () => {
        const formlistLength = state.formList.length
        onUpdateScheduling({
          ...state.formList[formlistLength - 1].data,
          activeStep: SCHEDULING_STEPS.DATE_AND_TIME,
          review: true
        })

        onGoTo(steps[5].path)
      }
      await redirectToServices(petUnselectedAndFormList ? handleRedirectPetStep : undefined)
    } else {
      // Quando samePetDate é true e o usuário está no step de profissional (step 5 no fluxo invertido),
      // deve voltar para o step de seleção de fluxo (step 3) para escolher novamente
      const isProfessionalStepInverted = state?.scheduleBody?.samePetDate && (activeStep === 5 || activeStep === 4)
      const shouldGoToFlowSelection = isProfessionalStepInverted && state?.formList?.length > 0

      // quando for multiplo agendamento, ao selecionar a mesma data do pet anterior e clicar em voltar no step de profissional precisa ser redirecionado para seleção de loja e não de data
      if ((activeStep - 1 === SCHEDULING_STEPS.PROFESSIONAL) && state?.scheduleBody?.samePetDate && state?.formList?.length > 0 && !shouldGoToFlowSelection) {
        const isCustomState = true
        editScheduling(SCHEDULING_STEPS.DATE_AND_TIME, isCustomState)
      } else if (shouldGoToFlowSelection) {
        // Quando samePetDate é true, volta para o step de seleção de fluxo
        editScheduling(3)
      } else {
        editScheduling(activeStep - 1)
      }
    }
  }

  const handleScheduleForMoreOnePet = async () => {
    const {
      currentPriceWithDiscount
    } = pricesInfo()

    context.showLoader()
    try {
      await handleAddPetToScheduling(currentPriceWithDiscount, onGoTo)
    } finally {
      context.hideLoader()
    }
  }

  const isDateAndTimeStepToPurchasedPackage = () => {
    return selectedPurchasedPackage && activeStep === PURCHASED_PACKAGE_SCHEDULING_STEPS.PET_AND_SERVICE
  }

  const isInvalidDateTerms = (date, time) => {
    const startOfDay = moment().startOf('day')
    const currentDate = moment()
    const schedulingDate = moment(date)
    const schedulingTime = moment(time, 'HH:mm')
    const schedulingDateTime = schedulingDate.clone().set({
      hour: schedulingTime.get('hour'),
      minute: schedulingTime.get('minute')
    })

    // Válida se a nova data do agendamento está dentro do tempo limite de captura
    const invalidDateTerm = schedulingDateTime.diff(startOfDay, 'days') > ONLINE_PAYMENT_DEADLINE

    // valida se agendamento esta a pelo menos 1 hora da data/hora atual
    const invalidTimeTerm = schedulingDateTime.diff(currentDate, 'hours') < SCHEDULING_MIN_HOURS

    return invalidDateTerm || invalidTimeTerm
  }

  const validateCurrentPayment = () => {
    const currentInvalidDateTerms = isInvalidDateTerms(scheduleBody.selectDay, scheduleBody.selectTime)
    const currentProfessionalPayment = isEntityEnabledForPayment(scheduleBody.professional_id, professionalData)
    const currentPartnerPayment = isEntityEnabledForPayment(scheduleBody.partner_id, partnersData)

    return !currentInvalidDateTerms && currentProfessionalPayment && currentPartnerPayment
  }

  const validateFormListPayment = () => {
    if (!formList?.length) {
      return true
    }

    const invalidPayment = formList.some(scheduling => {
      const { body, data } = scheduling
      const invalidDateTerm = isInvalidDateTerms(body.selectDay, body.selectTime)
      const professionalPayment = isEntityEnabledForPayment(body.professional_id, data.professionalData)
      const partnerPayment = isEntityEnabledForPayment(body.partner_id, data.partnersData)

      return invalidDateTerm || !professionalPayment || !partnerPayment
    })

    return !invalidPayment
  }

  const stepNavigationStrategies = {
    review: async () => {
      const shouldShowPaymentOptions = validateCurrentPayment() && validateFormListPayment()
      const isReschedule = router?.query?.id && router?.query?.reschedule && !hasSchedulingChanged(false, true)

      if (scheduleBody?.isSchedulingFromPackage) {
        handleConfirmScheduling({ rreschedule: router?.query?.reschedule === 'true', context, router })
        return
      }

      if (!shouldShowPaymentOptions || isReschedule) {
        if (!hasSchedulingChanged()) {
          router.push(`${PAGE_URL.SCHEDULING_SUMMARY}/${router?.query?.id}`)
        } else {
          handleConfirmScheduling({ reschedule: router?.query?.reschedule || false, context, router })
        }
      } else {
        calcNextStep(activeStep)
      }
    },

    payment: async () => {
      handleConfirmScheduling({ reschedule: router?.query?.reschedule || false, context, router })
    },

    purchasedPackage: async () => {
      context.showLoader()
      try {
        const newState = await findPartnerScheduleAndCampaignAviability(
          scheduleBody?.partner_id
        )
        onUpdateScheduling(newState)
        calcNextStep(activeStep)
      } finally {
        context.hideLoader()
      }
    },

    default: async () => {
      calcNextStep(activeStep)
    }
  }

  const getCurrentStepStrategy = () => {
    if (review) return 'review'
    if (isHowToPay) return 'payment'
    if (isDateAndTimeStepToPurchasedPackage()) return 'purchasedPackage'
    return 'default'
  }

  const handleNextButtonClick = async () => {
    const strategy = getCurrentStepStrategy()
    const stepHandler = stepNavigationStrategies[strategy]

    if (stepHandler) {
      await stepHandler()
    } else {
      await stepNavigationStrategies.default()
    }
  }

  return {
    activeStep,
    numberOfSteps,
    currentStep,
    nextDisabled,
    nextHidden,
    review,
    isHowToPay,
    finalStep,
    steps,
    backButtonProps,
    scheduleForMoreOnePetProps,
    nextButtonProps,
    confirmScheduleProps,
    handleClickBackButton,
    handleNextButtonClick,
    handleScheduleForMoreOnePet
  }
}

export default useSchedulingFormStep
